/**
 * useMaintenanceDraft — draft I/O for maintenance completions (FT-349c).
 *
 * Both `MaintenanceCheckView` (desktop) and `MaintenanceCheckViewMobile`
 * share one implementation. Key convention `maintenance_draft_${jobId}_${vesselId}`
 * aligns with existing `ra_draft_*` / `equipment_draft_*` conventions (see
 * DESIGN-BRIEF §3 rule 9).
 *
 * Sync exclusion: writes via plain `localStorage.setItem` — never routes
 * through `storage.js#writeJSON`, so no `sr:storage:set` event fires and
 * `UniversalSyncBridge` cannot see them.
 *
 * Draft body:
 *   { version, completionId, jobId, vesselId, savedAt, form, photos, viewState }
 *
 * FT-351 (2026-07-09): `viewState` records which sub-view the user was on
 * when autosave last fired — `'photos'` (main check view, photos visible) or
 * `'form-open'` (Complete-Check form open). Resume restores the same view so
 * a user who took photos + navigated away does not get dropped into the
 * sign-off form on return.
 *
 * API: draft, loading,
 *      saveDraft(formData, photos, completionId, viewState?),
 *      purgeDraft (cascade), discardDraft (key only), resumeDraft.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { devLog, devWarn } from '../../../utils/devLog';

export const DRAFT_KEY_PREFIX = 'maintenance_draft_';
export const DRAFT_VERSION = 1;
export const DRAFT_SAVE_DEBOUNCE_MS = 500;
export const DRAFT_STALE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// FT-351: view-state slots. `'photos'` = main check view (default, safer —
// user always lands on evidence photos first). `'form-open'` = Complete-Check
// sign-off form was open when autosave last fired.
export const VIEW_STATE_PHOTOS = 'photos';
export const VIEW_STATE_FORM_OPEN = 'form-open';
export const DEFAULT_VIEW_STATE = VIEW_STATE_PHOTOS;

/** Build the localStorage key for a job+vessel draft. */
export function draftKey(jobId, vesselId) {
  return `${DRAFT_KEY_PREFIX}${jobId}_${vesselId}`;
}

/** Read + parse a draft body; returns null on any failure. */
export function readDraft(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (err) {
    devWarn('[useMaintenanceDraft] read failed', key, err);
    return null;
  }
}

/**
 * Serialise + write. Uses plain `localStorage.setItem` — intentionally
 * bypasses `storageRouter.writeJSON` so no `sr:storage:set` event fires
 * (drafts must never sync to cloud, per DESIGN-BRIEF §2.4).
 */
export function writeDraft(key, body) {
  try {
    window.localStorage.setItem(key, JSON.stringify(body));
  } catch (err) {
    // Quota-exceeded is realistic. Warn but don't throw — blocking the form
    // would be worse UX than losing autosave.
    devWarn('[useMaintenanceDraft] write failed', key, err);
  }
}

/** Delete a draft key from localStorage. */
export function removeDraftKey(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (err) {
    devWarn('[useMaintenanceDraft] remove failed', key, err);
  }
}

/** Iterate all draft keys currently in localStorage. */
export function listDraftKeys() {
  const keys = [];
  try {
    const ls = window.localStorage;
    const len = ls.length || 0;
    for (let i = 0; i < len; i += 1) {
      const key = ls.key(i);
      if (key && key.startsWith(DRAFT_KEY_PREFIX)) keys.push(key);
    }
    // Fallback (jsdom-friendly): also enumerate own-property keys. Duplicates
    // are naturally filtered by the Set at the end.
    if (keys.length === 0) {
      const own = Object.keys(ls).filter(
        (k) => typeof k === 'string' && k.startsWith(DRAFT_KEY_PREFIX)
      );
      own.forEach((k) => keys.push(k));
    }
  } catch (err) {
    devWarn('[useMaintenanceDraft] listKeys failed', err);
  }
  return Array.from(new Set(keys));
}

/**
 * Boot-time stale sweep. For every draft older than `DRAFT_STALE_MS`,
 * cascade-delete its photos then remove the draft key. Standalone export
 * (not part of the hook) so it can be called once per session from an
 * app-boot / module-mount effect.
 *
 * @param {(recordType: string, recordId: string) => Promise<void>} deleteByRecord
 * @param {number} [now=Date.now()]
 * @returns {Promise<{swept: number, kept: number}>}
 */
export async function sweepStaleDrafts(deleteByRecord, now = Date.now()) {
  const keys = listDraftKeys();
  let swept = 0;
  let kept = 0;
  for (const key of keys) {
    const body = readDraft(key);
    if (!body || typeof body.savedAt !== 'number') {
      // Malformed — treat as stale, purge silently.
      removeDraftKey(key);
      swept += 1;
      continue;
    }
    if (now - body.savedAt < DRAFT_STALE_MS) {
      kept += 1;
      continue;
    }
    if (body.completionId && typeof deleteByRecord === 'function') {
      try {
        // eslint-disable-next-line no-await-in-loop
        await deleteByRecord('maintenance_job', body.completionId);
      } catch (err) {
        devWarn('[useMaintenanceDraft] stale photo cascade failed', body.completionId, err);
      }
    }
    removeDraftKey(key);
    swept += 1;
  }
  devLog(`[useMaintenanceDraft] stale sweep: ${swept} purged, ${kept} kept`);
  return { swept, kept };
}

/**
 * Hook: manage a single job+vessel draft.
 *
 * @param {string} jobId
 * @param {string} vesselId
 * @param {object} [options]
 * @param {(recordType, recordId) => Promise<void>} [options.deleteByRecord]
 *   Called by `purgeDraft` to cascade-delete photo blobs. Optional; a warning
 *   fires if `purgeDraft` runs without a handler.
 */
export function useMaintenanceDraft(jobId, vesselId, options = {}) {
  const { deleteByRecord } = options;

  const key = useMemo(
    () => (jobId && vesselId ? draftKey(jobId, vesselId) : null),
    [jobId, vesselId]
  );

  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);

  const debounceRef = useRef(null);
  const deleteByRecordRef = useRef(deleteByRecord);
  useEffect(() => {
    deleteByRecordRef.current = deleteByRecord;
  }, [deleteByRecord]);

  // Rehydrate on mount / key change.
  useEffect(() => {
    if (!key) {
      setDraft(null);
      setLoading(false);
      return;
    }
    const body = readDraft(key);
    setDraft(body);
    setLoading(false);
  }, [key]);

  // Cleanup any pending debounce on unmount.
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, []);

  /**
   * Debounced write. Callers pass current formData + photos + completionId
   * on every relevant change; only the last call within `DEBOUNCE_MS` wins.
   *
   * FT-351: optional `viewState` records which sub-view the user was on so
   * Resume restores the same screen. Defaults to `'photos'` (main view) when
   * omitted — safer, since resuming to the photos view never surprises the
   * user with a sign-off form they didn't open.
   */
  const saveDraft = useCallback(
    (formData, photos, completionId, viewState = DEFAULT_VIEW_STATE) => {
      if (!key || !jobId || !vesselId || !completionId) return;
      const safeViewState = viewState === VIEW_STATE_FORM_OPEN
        ? VIEW_STATE_FORM_OPEN
        : VIEW_STATE_PHOTOS;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        writeDraft(key, {
          version: DRAFT_VERSION,
          completionId,
          jobId,
          vesselId,
          savedAt: Date.now(),
          form: formData || {},
          photos: Array.isArray(photos) ? photos : [],
          viewState: safeViewState,
        });
        debounceRef.current = null;
      }, DRAFT_SAVE_DEBOUNCE_MS);
    },
    [key, jobId, vesselId]
  );

  /**
   * Full purge — cascade-delete photo blobs then remove the draft key.
   * Used on explicit Cancel-with-content and on Discard-from-rehydrate.
   */
  const purgeDraft = useCallback(async () => {
    if (!key) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const body = readDraft(key) || draft;
    const completionId = body?.completionId;
    if (completionId && typeof deleteByRecordRef.current === 'function') {
      try {
        await deleteByRecordRef.current('maintenance_job', completionId);
      } catch (err) {
        devWarn('[useMaintenanceDraft] purge cascade failed', completionId, err);
      }
    } else if (completionId) {
      devWarn(
        '[useMaintenanceDraft] purge called without deleteByRecord — photos may orphan',
        completionId
      );
    }
    removeDraftKey(key);
    setDraft(null);
  }, [key, draft]);

  /** Discard the key only (photos already committed). Used after Save. */
  const discardDraft = useCallback(async () => {
    if (!key) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    removeDraftKey(key);
    setDraft(null);
  }, [key]);

  /** Returns the currently-loaded draft body (or null). */
  const resumeDraft = useCallback(() => draft, [draft]);

  return { draft, loading, saveDraft, purgeDraft, discardDraft, resumeDraft };
}

export default useMaintenanceDraft;
